import { create } from 'zustand'
import { persist } from 'zustand/middleware'

// Default theme settings — Syrian Arab Republic / Ministry of Justice Brand Colors
// Primary: Deep Navy (#1a3a6b) | Secondary: Gold (#c9a227) | Both align with Syrian government palette
const DEFAULT_THEME = {
    primaryColor: '#1a3a6b',      // Deep Navy — Syrian government primary
    secondaryColor: '#c9a227',    // Gold — Syrian government accent
    successColor: '#1b7a3e',      // Green for success states
    warningColor: '#f59e0b',      // Amber warning
    errorColor: '#ef4444',        // Red error
    accentColor: '#0d2247',       // Dark navy accent
    darkColor: '#171717',         // Near-black
    fontFamily: 'Inter',
    borderRadius: 12,
    logoUrl: '/moj-logo.png',
    faviconUrl: '/moj-logo.png',
    // System Identity — Customizable from Admin → Settings → Theme
    systemNameEn: 'Ministry of Justice',
    systemNameAr: 'وزارة العدل',
}

/**
 * UI Store
 */
export const useUIStore = create(
    persist(
        (set, get) => ({
            // State
            language: 'en',
            darkMode: false,
            sidebarOpen: true,
            sidebarCollapsed: false,

            // Theme customization
            themeSettings: { ...DEFAULT_THEME },

            // Actions
            setLanguage: (language) => {
                set({ language })
                document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr'
                document.documentElement.lang = language
            },

            toggleLanguage: () => {
                const newLang = get().language === 'en' ? 'ar' : 'en'
                set({ language: newLang })
                document.documentElement.dir = newLang === 'ar' ? 'rtl' : 'ltr'
                document.documentElement.lang = newLang
            },

            setDarkMode: (darkMode) => {
                set({ darkMode })
                document.documentElement.classList.toggle('dark', darkMode)
            },

            toggleDarkMode: () => {
                const newMode = !get().darkMode
                set({ darkMode: newMode })
                document.documentElement.classList.toggle('dark', newMode)
            },

            setSidebarOpen: (open) => {
                set({ sidebarOpen: open })
            },

            toggleSidebar: () => {
                set((state) => ({ sidebarOpen: !state.sidebarOpen }))
            },

            setSidebarCollapsed: (collapsed) => {
                set({ sidebarCollapsed: collapsed })
            },

            toggleSidebarCollapsed: () => {
                set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed }))
            },

            // Theme customization actions
            setThemeSetting: (key, value) => {
                set((state) => ({
                    themeSettings: { ...state.themeSettings, [key]: value }
                }))
            },

            setThemeSettings: (settings) => {
                set((state) => ({
                    themeSettings: { ...state.themeSettings, ...settings }
                }))
            },

            resetThemeSettings: () => {
                set({ themeSettings: { ...DEFAULT_THEME } })
            },

            // Getters
            isRTL: () => get().language === 'ar',
        }),
        {
            name: 'ui-storage',
            partialize: (state) => ({
                language: state.language,
                darkMode: state.darkMode,
                sidebarCollapsed: state.sidebarCollapsed,
                themeSettings: state.themeSettings,
            }),
            onRehydrateStorage: () => (state) => {
                if (state) {
                    document.documentElement.dir = state.language === 'ar' ? 'rtl' : 'ltr'
                    document.documentElement.lang = state.language
                    document.documentElement.classList.toggle('dark', state.darkMode)
                }
            },
        }
    )
)

// Export default theme for reference
export const DEFAULT_THEME_SETTINGS = DEFAULT_THEME

export default useUIStore
